import React from 'react';
import { Calendar } from 'lucide-react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { AutocompleteSelect, type AutocompleteOption } from '../../components/common/AutocompleteSelect';
import { CreatableCombobox } from '../../components/common/CreatableCombobox';
import { countries } from '../../data/countries';
import type { GreenBeanProfileEnableFlags, GreenBeanProfileFormValues } from './useGreenBeanProfileForm';
import { useLanguage } from '../../features/language/useLanguage';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';

const PROCESS_TYPES = ['Washed', 'Honey', 'Natural', 'Other'];

const SPECIES_OPTIONS: AutocompleteOption[] = [
  { id: 'Arabica - Coffea Arabica', label: 'Arabica - Coffea Arabica', text: 'Arabica - Coffea Arabica' },
  { id: 'Canephora - Coffea Canephora', label: 'Canephora - Coffea Canephora', text: 'Canephora - Coffea Canephora' },
  { id: 'Liberica - Coffea Liberica', label: 'Liberica - Coffea Liberica', text: 'Liberica - Coffea Liberica' },
  { id: 'Excelsa - Coffea Excelsa', label: 'Excelsa - Coffea Excelsa', text: 'Excelsa - Coffea Excelsa' },
];

interface GreenBeanExtrinsicSectionProps {
  control: Control<GreenBeanProfileFormValues>;
  register: UseFormRegister<GreenBeanProfileFormValues>;
  uniqueOptions: {
    provinces: string[];
    regions: string[];
    farms: string[];
    producers: string[];
    species: string[];
    varieties: string[];
    processors: string[];
  };
  enableFlags: GreenBeanProfileEnableFlags;
  processType?: string;
  errors?: FieldErrors<GreenBeanProfileFormValues>;
}

export const GreenBeanExtrinsicSection: React.FC<GreenBeanExtrinsicSectionProps> = ({
  control,
  register,
  uniqueOptions,
  enableFlags,
  processType,
  errors,
}) => {
  const { t } = useLanguage();
  return (
  <div className="space-y-4">
    <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('greenBean.extrinsic.title')}</h2>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-4 w-4 shrink-0" />
            <div className="flex-1 flex items-center gap-4">
              <label className="w-40 shrink-0 text-sm font-medium text-gray-700">{t('greenBean.extrinsic.country')} <span className="text-red-500">*</span></label>
              <div className="flex-1">
                <Controller
                  control={control}
                  name="country"
                  rules={{ required: 'Required' }}
                  render={({ field, fieldState }) => (
                    <CreatableCombobox
                      label={t('greenBean.extrinsic.country')}
                      hideLabel
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={countries}
                      placeholder={t('greenBean.extrinsic.placeholderCountry')}
                      disabled={!enableFlags.enableCountry}
                      error={fieldState.error?.message}
                      dataTestId="green-bean-country-input"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pl-8">
            <div className="h-4 w-4 shrink-0" />
            <div className="flex-1 flex items-center gap-4">
              <label className="w-32 shrink-0 text-sm font-medium text-gray-700">{t('greenBean.extrinsic.province')} <span className="text-red-500">*</span></label>
              <div className="flex-1">
                <Controller
                  control={control}
                  name="province"
                  rules={{ required: 'Required' }}
                  render={({ field, fieldState }) => (
                    <CreatableCombobox
                      label={t('greenBean.extrinsic.province')}
                      hideLabel
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={uniqueOptions.provinces}
                      placeholder={t('greenBean.extrinsic.placeholderProvince')}
                      disabled={!enableFlags.enableProvince}
                      error={fieldState.error?.message}
                      dataTestId="green-bean-province-input"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pl-16">
            <div className="h-4 w-4 shrink-0" />
            <div className="flex-1 flex items-center gap-4">
              <label className="w-24 shrink-0 text-sm font-medium text-gray-700">{t('greenBean.extrinsic.region')} <span className="text-red-500">*</span></label>
              <div className="flex-1">
                <Controller
                  control={control}
                  name="region"
                  rules={{ required: 'Required' }}
                  render={({ field, fieldState }) => (
                    <CreatableCombobox
                      label={t('greenBean.extrinsic.region')}
                      hideLabel
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={uniqueOptions.regions}
                      placeholder={t('greenBean.extrinsic.placeholderRegion')}
                      disabled={!enableFlags.enableRegion}
                      error={fieldState.error?.message}
                      dataTestId="green-bean-region-input"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            {...register('_enable_farm')}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            data-test-id="green-bean-enable-farm"
          />
          <div className="flex-1 flex items-center gap-4">
            <label className="w-40 shrink-0 text-sm font-medium text-gray-700">{t('greenBean.extrinsic.farm')}</label>
            <div className="flex-1">
              <Controller
                control={control}
                name="farm_name"
                render={({ field }) => (
                  <CreatableCombobox
                    label={t('greenBean.extrinsic.farm')}
                    hideLabel
                    value={field.value || ''}
                    onChange={field.onChange}
                    options={uniqueOptions.farms}
                    placeholder={t('greenBean.extrinsic.placeholderFarm')}
                    disabled={!enableFlags.enableFarm}
                    dataTestId="green-bean-farm-input"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            {...register('_enable_producer')}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            data-test-id="green-bean-enable-producer"
          />
          <div className="flex-1 flex items-center gap-4">
            <label className="w-40 shrink-0 text-sm font-medium text-gray-700">{t('inventory.modal.fields.producer')}</label>
            <div className="flex-1">
              <Controller
                control={control}
                name="producer"
                render={({ field }) => (
                  <CreatableCombobox
                    label={t('inventory.modal.fields.producer')}
                    hideLabel
                    value={field.value || ''}
                    onChange={field.onChange}
                    options={uniqueOptions.producers}
                    placeholder={t('inventory.modal.fields.producerPlaceholder')}
                    disabled={!enableFlags.enableProducer}
                    dataTestId="green-bean-producer-input"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-4 w-4 shrink-0" />
            <div className="flex-1 flex items-center gap-4">
              <label className="w-40 shrink-0 text-sm font-medium text-gray-700">{t('inventory.modal.fields.species')} <span className="text-red-500">*</span></label>
              <div className="flex-1">
                <Controller
                  control={control}
                  name="species"
                  rules={{ required: 'Required' }}
                  render={({ field, fieldState }) => (
                    <AutocompleteSelect
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={SPECIES_OPTIONS}
                      placeholder={t('inventory.modal.fields.speciesPlaceholder')}
                      disabled={!enableFlags.enableSpecies}
                      error={fieldState.error?.message}
                      dataTestId="green-bean-species-input"
                      allowCustom={false}
                      optionsClassName="min-w-full w-max"
                      optionClassName="whitespace-nowrap"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 pl-8">
            <input
              type="checkbox"
              {...register('_enable_variety')}
              disabled={!enableFlags.enableSpecies}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              data-test-id="green-bean-enable-variety"
            />
            <div className="flex-1 flex items-center gap-4">
              <label className="w-32 shrink-0 text-sm font-medium text-gray-700">{t('inventory.modal.fields.variety')}</label>
              <div className="flex-1">
                <Controller
                  control={control}
                  name="variety"
                  render={({ field }) => (
                    <CreatableCombobox
                      label={t('inventory.modal.fields.variety')}
                      hideLabel
                      value={field.value}
                      onChange={field.onChange}
                      options={uniqueOptions.varieties}
                      placeholder={t('inventory.modal.fields.varietyPlaceholder')}
                      disabled={!enableFlags.enableVariety}
                      dataTestId="green-bean-variety-input"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            {...register('_enable_harvest_date')}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            data-test-id="green-bean-enable-harvest-date"
          />
          <div className="flex-1 flex items-center gap-4">
            <label
              className="w-40 shrink-0 text-sm font-medium text-gray-700"
            >
              {t('greenBean.extrinsic.harvestDateYear')}
            </label>
            <div className="relative flex-1">
              <input
                type="date"
                {...register('harvest_date')}
                disabled={!enableFlags.enableHarvestDate}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-transparent"
                data-test-id="green-bean-harvest-date-input"
              />
              {!enableFlags.enableHarvestDate && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <Calendar className="h-4 w-4 text-gray-400" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              {...register('_enable_processor')}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              data-test-id="green-bean-enable-processor"
            />
            <div className="flex-1 flex items-center gap-4">
              <label className="w-40 shrink-0 text-sm font-medium text-gray-700">{t('greenBean.extrinsic.processor')}</label>
              <div className="flex-1">
                <Controller
                  control={control}
                  name="processor"
                  render={({ field }) => (
                    <CreatableCombobox
                      label={t('greenBean.extrinsic.processor')}
                      hideLabel
                      value={field.value}
                      onChange={field.onChange}
                      options={uniqueOptions.processors}
                      placeholder={t('greenBean.extrinsic.placeholderProcessor')}
                      disabled={!enableFlags.enableProcessor}
                      dataTestId="green-bean-processor-input"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="pl-8 space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('_enable_wet_mill')}
                disabled={!enableFlags.enableProcessor}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                data-test-id="green-bean-enable-wet-mill"
              />
              <div className="flex-1 flex items-center gap-4">
                <label
                  className={`w-32 shrink-0 text-sm font-medium ${
                    enableFlags.enableWetMill ? 'text-gray-700' : 'text-gray-400'
                  }`}
                >
                  {t('greenBean.extrinsic.wetMill')}
                </label>
                <input
                  type="text"
                  {...register('wet_mill')}
                  disabled={!enableFlags.enableWetMill}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-400"
                  data-test-id="green-bean-wet-mill-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('_enable_dry_mill')}
                disabled={!enableFlags.enableProcessor}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                data-test-id="green-bean-enable-dry-mill"
              />
              <div className="flex-1 flex items-center gap-4">
                <label
                  className={`w-32 shrink-0 text-sm font-medium ${
                    enableFlags.enableDryMill ? 'text-gray-700' : 'text-gray-400'
                  }`}
                >
                  {t('greenBean.extrinsic.dryMill')}
                </label>
                <input
                  type="text"
                  {...register('dry_mill')}
                  disabled={!enableFlags.enableDryMill}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-400"
                  data-test-id="green-bean-dry-mill-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('_enable_processing_other')}
                disabled={!enableFlags.enableProcessor}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                data-test-id="green-bean-enable-processing-other"
              />
              <div className="flex-1 flex items-center gap-4">
                <label
                  className={`w-32 shrink-0 text-sm font-medium ${
                    enableFlags.enableProcessingOther ? 'text-gray-700' : 'text-gray-400'
                  }`}
                >
                  {t('greenBean.extrinsic.other')}
                </label>
                <input
                  type="text"
                  {...register('processing_other')}
                  disabled={!enableFlags.enableProcessingOther}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-400"
                  data-test-id="green-bean-processing-other-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-3">
            <div className="h-4 w-4 shrink-0 mt-1" />
            <label className="text-sm font-medium text-gray-900">
              {t('inventory.modal.fields.processType')} <span className="text-red-500">*</span>
            </label>
          </div>

          <div className="ml-8 flex flex-col gap-2">
            {PROCESS_TYPES.map((type) => {
              const isOther = type === 'Other';
              const isEnabled = enableFlags.enableProcessType;
              const selected = processType === 'Other';

              return (
                <div key={type} className="flex items-center gap-3">
                  <div className="flex items-center gap-2 w-40 shrink-0">
                    <input
                      type="radio"
                      value={type}
                      {...register('process_type', { required: 'Required' })}
                      disabled={!isEnabled}
                      id={`process-type-${type}`}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                      data-test-id={`green-bean-process-type-${type}`}
                    />
                    <label
                      htmlFor={`process-type-${type}`}
                      className={`ml-1 text-sm ${isEnabled ? 'text-gray-700' : 'text-gray-400'}`}
                    >
                      {type}
                    </label>
                  </div>

                  <div className="flex-1 min-h-[40px]">
                    {isOther && selected && (
                      <input
                        type="text"
                        placeholder={t('inventory.modal.fields.processTypePlaceholder')}
                        {...register('process_type_other', {
                          required: t('greenBean.extrinsic.errors.specifyProcessType'),
                        })}
                        disabled={!isEnabled}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100"
                        data-test-id="green-bean-process-type-other"
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {errors?.process_type && (
              <p className="text-red-500 text-xs mt-1">{errors.process_type.message as string}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};
